import React from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, RefreshCw, CalendarIcon } from "lucide-react";
import { Leave } from "@/components/pto/types";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";

// Date formatting utilities
const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString);
  return format(date, "dd/MM/yyyy");
};

interface DateFilterForm {
  employeeName: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
}

const RenderFilters: React.FC<{
  filterEmployee: string;
  setFilterEmployee: (value: string) => void;
  filterStartDate: Date | undefined;
  setFilterStartDate: (value: Date | undefined) => void;
  filterEndDate: Date | undefined;
  setFilterEndDate: (value: Date | undefined) => void;
  onUpdate: () => void;
  isLoading: boolean;
}> = ({
  filterEmployee,
  setFilterEmployee,
  filterStartDate,
  setFilterStartDate,
  filterEndDate,
  setFilterEndDate,
  onUpdate,
  isLoading,
}) => {
  const form = useForm<DateFilterForm>({
    defaultValues: {
      employeeName: filterEmployee,
      startDate: filterStartDate,
      endDate: filterEndDate,
    },
  });

  return (
    <Form {...form}>
      <div className="flex flex-col md:flex-row md:items-end md:gap-4 mb-4">
        <div className="flex flex-1 flex-col sm:flex-row gap-4">
          <FormField
            control={form.control}
            name="employeeName"
            render={({ field }) => (
              <FormItem className="flex-1 min-w-[150px]">
                <FormLabel>Employee Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Filter by employee name"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setFilterEmployee(e.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex-1 min-w-[150px]">
                <FormLabel>Start Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        {field.value ? (
                          format(field.value, "dd/MM/yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setFilterStartDate(date);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex-1 min-w-[150px]">
                <FormLabel>End Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        {field.value ? (
                          format(field.value, "dd/MM/yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setFilterEndDate(date);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          onClick={onUpdate}
          disabled={isLoading}
          className="w-full md:w-auto mt-4 md:mt-0"
          type="button"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Update
        </Button>
      </div>
    </Form>
  );
};

const RenderLeavesTable: React.FC<{
  leaves: Leave[];
  filterEmployee: string;
  isLoading: boolean;
}> = ({ leaves, filterEmployee, isLoading }) => {
  const filteredLeaves = leaves.filter((leave) =>
    leave.eeDisplayName.toLowerCase().includes(filterEmployee.toLowerCase()),
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Employee Name</TableHead>
          <TableHead>Leave Type</TableHead>
          <TableHead>Start Date</TableHead>
          <TableHead>End Date</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center">
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Loading...</span>
              </div>
            </TableCell>
          </TableRow>
        ) : filteredLeaves.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-4">
              No leaves found
            </TableCell>
          </TableRow>
        ) : (
          filteredLeaves.map((leave, index) => (
            <TableRow key={index}>
              <TableCell>{leave.eeDisplayName}</TableCell>
              <TableCell>{leave.leaveTypeVersion.displayName}</TableCell>
              <TableCell>{formatDateForDisplay(leave.startDate)}</TableCell>
              <TableCell>{formatDateForDisplay(leave.endDate)}</TableCell>
              <TableCell>{leave.latestStatus}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

const RenderAllLeavesCard: React.FC<{
  filterEmployee: string;
  setFilterEmployee: (value: string) => void;
  filterStartDate: Date | undefined;
  setFilterStartDate: (value: Date | undefined) => void;
  filterEndDate: Date | undefined;
  setFilterEndDate: (value: Date | undefined) => void;
  leaves: Leave[];
  onUpdate: () => void;
  isLoading: boolean;
}> = ({
  filterEmployee,
  setFilterEmployee,
  filterStartDate,
  setFilterStartDate,
  filterEndDate,
  setFilterEndDate,
  leaves,
  onUpdate,
  isLoading,
}) => (
  <Card>
    <CardHeader>
      <h2 className="text-xl">All Leaves</h2>
    </CardHeader>
    <CardContent>
      <RenderFilters
        filterEmployee={filterEmployee}
        setFilterEmployee={setFilterEmployee}
        filterStartDate={filterStartDate}
        setFilterStartDate={setFilterStartDate}
        filterEndDate={filterEndDate}
        setFilterEndDate={setFilterEndDate}
        onUpdate={onUpdate}
        isLoading={isLoading}
      />
      <RenderLeavesTable
        leaves={leaves}
        filterEmployee={filterEmployee}
        isLoading={isLoading}
      />
    </CardContent>
  </Card>
);

export default RenderAllLeavesCard;
